/**
 * Pure stacking rules for in-meeting notices, kept apart from React and from
 * wherever a notice gets raised so the cap-and-replace behaviour can be
 * tested directly instead of through a mounted toast.
 *
 * Newest goes to the front of the list. Once three are open, a fourth push
 * drops the oldest rather than growing the stack further: a meeting busy
 * enough to raise several notices a minute would otherwise bury the control
 * bar in them. An update for an id already in the list (the same background
 * job moving from "applying" to "done") replaces that entry where it already
 * sits instead of jumping it back to the front, so a notice someone has
 * started reading does not keep relocating under them.
 *
 * A notice with no fixed duration (`Infinity`, used for a "working on it"
 * state that lasts until something replaces it) gets no auto-close timer of
 * its own, but it is not exempt from the cap: it ages out the same as
 * anything else once a fourth notice arrives. Nothing here owns a clock:
 * eviction only ever happens as a side effect of `pushNotice`.
 */

export type NoticeVariant = 'success' | 'error' | 'warning' | 'info' | 'pending' | 'default'

export interface NoticeAction {
  label: string
  onClick: () => void
}

export interface MeetingNotice {
  id: string | number
  title: string
  description?: string
  variant: NoticeVariant
  /** Milliseconds before auto-dismiss, or Infinity to wait for an update or an explicit dismiss. */
  duration: number
  action?: NoticeAction
}

/** At most this many ordinary notices on screen at once. */
export const MAX_ORDINARY_NOTICES = 3

/**
 * Add or replace a notice. A matching id is updated in place (same position,
 * new content); anything new is inserted at the front and the list is
 * trimmed back to the cap, dropping whatever fell off the end.
 */
export function pushNotice(list: readonly MeetingNotice[], notice: MeetingNotice): MeetingNotice[] {
  const existingIndex = list.findIndex((n) => n.id === notice.id)
  if (existingIndex !== -1) {
    const next = list.slice()
    next[existingIndex] = notice
    return next
  }
  const next = [notice, ...list]
  return next.length > MAX_ORDINARY_NOTICES ? next.slice(0, MAX_ORDINARY_NOTICES) : next
}

/** Remove one notice by id. A no-op if it is not (or is no longer) there. */
export function dismissNotice(list: readonly MeetingNotice[], id: string | number): MeetingNotice[] {
  return list.filter((n) => n.id !== id)
}

/** "Clear all" for the ordinary stack: always empties it. */
export function clearOrdinary(): MeetingNotice[] {
  return []
}
